import React, { useMemo } from "react";

import { RFI_STATUS_COLORS_LIGHT, RFI_STATUS_LABELS } from "../../operations/rfi/constants";
import { CompactTicketSummaryCard } from "../../tickets";
import { formatDate } from "../../../utils/datetime";
import { useProjectPageContext } from "./ProjectPageContext";

/**
 * Open a React Router `/app` path in a new browser tab.
 *
 * @param {string} appPath Path under the `/app` basename (e.g. `/operations/rfi/1`).
 * @returns {void}
 */
function openAppPathInNewTab(appPath) {
  window.open(`/app${appPath}`, "_blank", "noopener,noreferrer");
}

/**
 * Format a number as compact US currency for project ticket metrics.
 *
 * @param {number} value - Numeric currency value.
 * @returns {string}
 */
function formatCurrencyMetric(value) {
  return value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

/**
 * Return a simple read-only status label for a T&M report.
 *
 * @param {Object} report - T&M report row.
 * @returns {string}
 */
function getTMStatusLabel(report) {
  if (report.submitted) {
    return "Submitted";
  }
  if (report.pricing) {
    return "Priced";
  }
  return "Open";
}

/**
 * Return Tailwind color classes for a read-only T&M status pill.
 *
 * @param {Object} report - T&M report row.
 * @returns {string}
 */
function getTMStatusClassName(report) {
  if (report.submitted) {
    return "bg-green-100 text-green-800";
  }
  if (report.pricing) {
    return "bg-amber-100 text-amber-800";
  }
  return "bg-gray-100 text-gray-700";
}

/**
 * Render a small status pill for project ticket summary rows.
 *
 * @param {Object} props - Component props.
 * @param {string} props.label - Status label.
 * @param {string} props.className - Status color classes.
 * @returns {JSX.Element}
 */
const ProjectTicketStatusPill = ({ label, className }) => (
  <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${className}`}>
    {label}
  </span>
);

/**
 * Return whether an RFI is overdue and still open.
 *
 * @param {Object} rfi - RFI row.
 * @returns {boolean}
 */
function isOverdueRFI(rfi) {
  if (!rfi.required_by_date || rfi.status === "closed") {
    return false;
  }
  const dueDate = new Date(rfi.required_by_date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  dueDate.setHours(0, 0, 0, 0);
  return dueDate < today;
}

/**
 * Build compact T&M metric tiles.
 *
 * @param {Array<Object>} reports - Project-scoped T&M rows.
 * @returns {Array<Object>}
 */
function getTMMetrics(reports) {
  const totalValue = reports.reduce(
    (sum, report) => sum + Number(report.pricing?.total_cost || 0),
    0
  );
  return [
    {
      value: reports.length,
      label: "Total",
    },
    {
      value: reports.filter((report) => report.pricing).length,
      label: "Priced",
    },
    {
      value: reports.filter((report) => report.submitted).length,
      label: "Submitted",
    },
    {
      value: formatCurrencyMetric(totalValue),
      label: "Value",
    },
  ];
}

/**
 * Build compact RFI metric tiles.
 *
 * @param {Array<Object>} rfis - Project-scoped RFI rows.
 * @returns {Array<Object>}
 */
function getRFIMetrics(rfis) {
  return [
    {
      value: rfis.length,
      label: "Total",
    },
    {
      value: rfis.filter((rfi) => rfi.status !== "closed").length,
      label: "Open",
    },
    {
      value: rfis.filter((rfi) => rfi.status === "awaiting_response").length,
      label: "Awaiting",
    },
    {
      value: rfis.filter(isOverdueRFI).length,
      label: "Overdue",
    },
  ];
}

/**
 * Return compact table columns for project T&M summary cards.
 *
 * @returns {Array<Object>}
 */
function getTMColumns() {
  return [
    {
      key: "tm_id",
      label: "T&M",
      sortable: true,
      cellClassName: "px-3 py-3 font-mono text-xs font-semibold text-green-700 whitespace-nowrap",
      render: (item) => item.tm_id || "-",
    },
    {
      key: "status",
      label: "Status",
      sortable: false,
      cellClassName: "px-3 py-3 whitespace-nowrap",
      render: (item) => (
        <ProjectTicketStatusPill
          label={getTMStatusLabel(item)}
          className={getTMStatusClassName(item)}
        />
      ),
    },
    {
      key: "date_performed",
      label: "Date",
      sortable: true,
      cellClassName: "px-3 py-3 text-xs text-gray-600 whitespace-nowrap",
      render: (item) => (
        item.date_performed ? formatDate(item.date_performed, "M/D/YY") : "-"
      ),
    },
    {
      key: "description",
      label: "Description",
      sortable: true,
      cellClassName: "px-3 py-3 max-w-[220px]",
      render: (item) => (
        <div className="line-clamp-2 text-xs text-gray-700">
          {item.description || "No description"}
        </div>
      ),
    },
    {
      key: "amount",
      label: "Amount",
      sortable: true,
      hideOnMobile: true,
      cellClassName: "px-3 py-3 text-xs font-semibold text-green-700 whitespace-nowrap",
      getSortValue: (item) => Number(item.pricing?.total_cost || 0),
      render: (item) => (
        item.pricing?.total_cost == null
          ? "Not priced"
          : formatCurrencyMetric(Number(item.pricing.total_cost))
      ),
    },
  ];
}

/**
 * Return compact table columns for project RFI summary cards.
 *
 * @returns {Array<Object>}
 */
function getRFIColumns() {
  return [
    {
      key: "rfi_id",
      label: "RFI",
      sortable: true,
      cellClassName: "px-3 py-3 font-mono text-xs font-semibold text-blue-700 whitespace-nowrap",
      render: (item) => item.rfi_id || "-",
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      cellClassName: "px-3 py-3 whitespace-nowrap",
      render: (item) => {
        const status = item.status || "new";
        return (
          <ProjectTicketStatusPill
            label={RFI_STATUS_LABELS[status] || status}
            className={RFI_STATUS_COLORS_LIGHT[status] || "bg-gray-100 text-gray-700"}
          />
        );
      },
    },
    {
      key: "subject",
      label: "Summary",
      sortable: true,
      cellClassName: "px-3 py-3 max-w-[260px]",
      render: (item) => (
        <div className="line-clamp-2 text-xs text-gray-700">
          {item.subject || item.description || "No summary"}
        </div>
      ),
    },
    {
      key: "required_by_date",
      label: "Due",
      sortable: true,
      hideOnMobile: true,
      cellClassName: "px-3 py-3 text-xs whitespace-nowrap",
      render: (item) => {
        if (!item.required_by_date) {
          return "-";
        }
        return (
          <span className={isOverdueRFI(item) ? "font-semibold text-red-600" : "text-gray-600"}>
            {formatDate(item.required_by_date, "M/D/YY")}
          </span>
        );
      },
    },
  ];
}

/**
 * Render compact project-scoped T&M and RFI summary cards.
 *
 * @returns {JSX.Element}
 */
const ProjectTicketSummaries = () => {
  const { placeholderContent, tmTicketSummary, rfiTicketSummary } =
    useProjectPageContext();
  const { overview } = placeholderContent;
  const tmColumns = useMemo(getTMColumns, []);
  const rfiColumns = useMemo(getRFIColumns, []);
  const tmMetrics = useMemo(
    () => getTMMetrics(tmTicketSummary.items),
    [tmTicketSummary.items]
  );
  const rfiMetrics = useMemo(
    () => getRFIMetrics(rfiTicketSummary.items),
    [rfiTicketSummary.items]
  );

  return (
    <div className="grid gap-2 lg:grid-cols-2">
      <CompactTicketSummaryCard
        title={overview.tmLog.title}
        subtitle={overview.tmLog.subtitle}
        data={tmTicketSummary.items}
        columns={tmColumns}
        metrics={tmMetrics}
        itemLabel="T&M tag"
        emptyMessage={tmTicketSummary.error || "No T&M reports for this project"}
        defaultSort={{ field: "date_performed", direction: "desc" }}
        searchFields={["tm_id", "description"]}
        isLoading={tmTicketSummary.isLoading}
        onRowClick={(item) =>
          openAppPathInNewTab(`/operations/tm-log/report/${item.id}`)
        }
        accentClassName="text-green-800"
        selectFocusClassName="focus:border-green-500 focus:ring-green-500"
      />

      <CompactTicketSummaryCard
        title={overview.rfiLog.title}
        subtitle={overview.rfiLog.subtitle}
        data={rfiTicketSummary.items}
        columns={rfiColumns}
        metrics={rfiMetrics}
        itemLabel="RFI"
        emptyMessage={rfiTicketSummary.error || "No RFIs for this project"}
        defaultSort={{ field: "required_by_date", direction: "asc" }}
        searchFields={["rfi_id", "subject", "description"]}
        isLoading={rfiTicketSummary.isLoading}
        onRowClick={(item) =>
          openAppPathInNewTab(`/operations/rfi/${item.id}`)
        }
        accentClassName="text-blue-800"
        selectFocusClassName="focus:border-blue-500 focus:ring-blue-500"
      />
    </div>
  );
};

export default ProjectTicketSummaries;
